import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';
import { Mail } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        pageType="custom"
        title="Terms & Conditions | GoCompareAI"
        description="The terms that govern use of the GoCompareAI platform, including comparisons, vendor information, and user responsibilities."
      />
      <Header />
      
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
            Terms & Conditions
          </h1>
          
          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Overview</h2>
              <p className="text-muted-foreground leading-relaxed">
                GoCompareAI provides an independent platform for comparing AI tools and platforms. By using this website, you agree to these terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Use of the Platform</h2>
              <p className="text-muted-foreground leading-relaxed">
                You may use GoCompareAI for informational and comparison purposes only. You agree not to misuse the platform or attempt to interfere with its operation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">AI Search Assistant</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our AI-powered search feature helps you find relevant AI platforms using natural language queries. By using this feature, you agree to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Use the search feature only for legitimate purposes</li>
                <li>Not attempt to use the AI to generate harmful, illegal, or inappropriate content</li>
                <li>Accept that AI recommendations are suggestions only, not endorsements</li>
                <li>Understand that AI responses may not always be accurate or complete</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Acceptable Use Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree not to use the platform, including the AI search feature, for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Any illegal activities or purposes</li>
                <li>Finding tools to create fraudulent, deceptive, or harmful content</li>
                <li>Harassment, hate speech, or threatening behaviour</li>
                <li>Any activity that could harm others or violate their rights</li>
                <li>Attempting to circumvent our safety measures or content moderation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Content Moderation</h2>
              <p className="text-muted-foreground leading-relaxed">
                We employ automated content moderation to ensure platform safety. Queries that violate our acceptable use policy may be blocked, and repeat violations may result in restricted access. We reserve the right to refuse service to anyone who violates these terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">No Endorsement or Advice</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>GoCompareAI does not provide legal, financial, or technical advice.</li>
                <li>Listings and comparisons do not constitute endorsements or recommendations.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Accuracy of Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                We aim to present accurate and up-to-date information, but we cannot guarantee completeness or accuracy at all times. Users should verify details directly with vendors.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Vendor Content</h2>
              <p className="text-muted-foreground leading-relaxed">
                Some information is provided by vendors. Vendors are responsible for the accuracy of their own content.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                GoCompareAI is not liable for decisions made based on information provided on the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All site content, branding, and materials belong to GoCompareAI unless otherwise stated.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Changes to These Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update these terms from time to time. Continued use of the site indicates acceptance of any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Questions regarding these terms can be sent to:
              </p>
              <a 
                href="mailto:info@gocompareai.com" 
                className="inline-flex items-center gap-2 text-primary hover:underline mt-2"
              >
                <Mail className="w-4 h-4" />
                info@gocompareai.com
              </a>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
